import React from 'react';
import styled from 'styled-components';
import { IoBusinessOutline, IoHomeOutline, IoArrowForward } from 'react-icons/io5';
import { devices } from '../data/constants';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;

  @media only screen and ${devices.md} {
    padding: 20px 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media only screen and ${devices.md} {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0 0 12px 0;

  @media only screen and ${devices.md} {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0;

  @media only screen and ${devices.md} {
    font-size: 14px;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media only screen and ${devices.md} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ModeCard = styled.div`
  position: relative;
  padding: 32px 28px;
  border: 2px solid ${({ $isSelected, theme }) => 
    $isSelected ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.cardBG};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 220px;
  box-shadow: ${({ $isSelected }) => 
    $isSelected ? '0 4px 20px rgba(0, 123, 255, 0.2)' : 'none'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.activeColor};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  @media only screen and ${devices.md} {
    padding: 24px 20px;
    min-height: 180px;
  }
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${({ $isSelected, theme }) => 
    $isSelected ? theme.colors.activeColor : theme.colors.opacityColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: background 0.3s ease;

  svg {
    color: ${({ $isSelected, theme }) => 
      $isSelected ? '#fff' : theme.colors.activeColor};
    transition: color 0.3s ease;
  }

  @media only screen and ${devices.md} {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }
`;

const CardTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0 0 8px 0;

  @media only screen and ${devices.md} {
    font-size: 18px;
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0;
  line-height: 1.5;
  flex: 1;

  @media only screen and ${devices.md} {
    font-size: 13px;
  }
`;

const CardFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.subTextColor};

  li {
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '✓';
      color: ${({ theme }) => theme.colors.activeColor};
      font-weight: 600;
    }
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 12px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  opacity: ${({ $show }) => $show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  margin: 40px auto 0;
  padding: 16px 32px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.colors.ff};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.activeColorDarkened};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media only screen and ${devices.md} {
    margin-top: 24px;
    padding: 14px 28px;
  }
`;

const ModeSelectorNew = ({ mode, setMode, onStart }) => {
  const modes = [
    {
      id: 'zip',
      title: 'ZIP Code Screening',
      description: 'Screen multiple markets to find properties that meet your investment criteria.',
      icon: IoBusinessOutline,
      features: [
        'Analyze multiple ZIP codes at once',
        'Auto-fetch market data via AI',
        'Filter by Cash-on-Cash threshold',
        'See all passed listings'
      ]
    },
    {
      id: 'manual',
      title: 'Single Property Analysis',
      description: 'Deep dive into a specific property with complete financial projections.',
      icon: IoHomeOutline,
      features: [
        'Full amortization schedule',
        'Year-by-year cash flow',
        'IRR & COC projections',
        'Valuation at sale'
      ]
    }
  ];

  return (
    <Container>
      <Header>
        <Title>Choose Your Analysis Type</Title>
        <Subtitle>Select how you want to analyze your investment opportunity</Subtitle>
      </Header>

      <CardsContainer>
        {modes.map((m) => (
          <ModeCard
            key={m.id}
            $isSelected={mode === m.id}
            onClick={() => setMode(m.id)}
          >
            <SelectedBadge $show={mode === m.id}>Selected</SelectedBadge>
            <IconContainer $isSelected={mode === m.id}>
              <m.icon size={28} />
            </IconContainer>
            <CardTitle>{m.title}</CardTitle>
            <CardDescription>{m.description}</CardDescription>
            <CardFeatures>
              {m.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </CardFeatures>
          </ModeCard>
        ))}
      </CardsContainer>

      <StartButton onClick={onStart}>
        Start Analysis
        <IoArrowForward size={20} />
      </StartButton>
    </Container>
  );
};

export default ModeSelectorNew;
